import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { getApiUrl } from '@shared/utils/app-util';

export interface UpdateSignupStatusResponse {
 message: string;
 user_id: number;
 status: string;
 admin_remark: string;
 updated_at: string;
}

export interface CertificateInfo {
 status: string;
 message: string;
 url: string | null;
 uploaded_at: string;
}

export interface SignupRequest {
 id: number;
 email: string;
 first_name: string;
 last_name: string;
 role_id: number;
 country_id: number;
 assigned_region_id: number;
 company_name: string;
 status: string;
 active_till: string | null;
 added_at: string | null;
 admin_remark: string | null;
 // Backward compatibility: some environments may still return certificate_url/certificate_status
 certificate_url?: string | null;
 requested_at: string;
 signup_method: string;
 certificate_status?: string | null;
 certificate_info?: CertificateInfo | null;
 created_at: string;
}


export interface OnboardedUser {
 id: number;
 email: string;
 first_name: string;
 last_name: string;
 role_id: number;
 country_id: number; // User's primary country
 responsible_region_id: number;
 responsible_country_ids: number[];
 assigned_region_id: number;
 company_name: string;
 status: string; // Training status (e.g., "Active", "Inactive")
 active_till: string | null;
 added_at: string | null;
 admin_remark: string | null;
 // Backward compatibility
 certificate_url?: string | null;
 requested_at: string | null;
 signup_method: string;
 certificate_status?: string | null;
 certificate_info?: CertificateInfo | null;
 created_at: string;
}

export interface GetOnboardedUsersResponse {
 message: string;
 users: OnboardedUser[]; // Note: the API returns 'users' not 'signup_requests'
 meta: PaginationMeta;
}

export interface PaginationMeta {
 total: number;
 page: number;
 page_size: number;
 total_pages: number;
 count: number;
}

export interface GetSignupRequestsResponse {
 message: string;
 signup_requests: SignupRequest[];
 meta: PaginationMeta;
}
export interface CertificateData {
 bucket: string;
 name: string;
}

export interface SignupRequestPayload {
 // New API expects certificate_data; keep certificate_url optional for backward compatibility
 certificate_data?: CertificateData;
 certificate_url?: string;
 country_id: number;
 email: string;
 first_name: string;
 last_name: string;
 responsible_country_ids: number[];
 responsible_region_id: number;
 role_id: number;
 company_name: string;
 end_date?: string;
}

export interface SignupResponse {
 message: string;
 user_id: number;
 status: string;
}

export interface Region {
 id: number;
 name: string;
}
export interface GetRegionsResponse {
 message: string;
 regions: Region[];
}

export interface UserRole {
 id: number;
 name: string;
}

export interface GetUserRolesResponse {
 message: string;
 user_roles: UserRole[];
}

export interface Country {
 id: number;
 name: string;
 region: {
  id: number;
  name: string;
 };
 added_at: string;
}

export interface GetCountriesResponse {
 message: string;
 countries: Country[];
}

export interface UserUpdatePayload {
 first_name?: string;
 last_name?: string;
 company_name?: string;
 country_id?: number;
 responsible_region_id?: number;
 responsible_country_ids?: number[];
 active_till?: string; // ISO string date
 status?: 'active' | 'inactive' | 'pending';
 admin_remark?: string;
 role_id?: number;
}

export interface CreateUserResponse {
 message: string;
 user: OnboardedUser;
}

@Injectable({
 providedIn: 'root'
})
export class ApiService {

 constructor(private readonly http: HttpClient) { }

 /**
 * Sends a user signup request to the backend.
 * @param payload The signup request data.
 * @returns An Observable of the signup response.
 */
 signupUser(payload: SignupRequestPayload): Observable<SignupResponse> {
  const url = getApiUrl('SIGNUP_REQUEST');
  return this.http.post<SignupResponse>(url, payload);
 }

 /**
 * Fetches the list of regions from the backend.
 * @returns An Observable of the regions response.
 */
 getRegions(): Observable<GetRegionsResponse> {
  const url = getApiUrl('GET_REGIONS');
  return this.http.get<GetRegionsResponse>(url);
 }

 getUserRoles(): Observable<GetUserRolesResponse> {
  const url = getApiUrl('GET_USER_ROLES');
  return this.http.get<GetUserRolesResponse>(url);
 }

 getCountries(): Observable<GetCountriesResponse> {
  const url = getApiUrl('GET_COUNTRIES');
  return this.http.get<GetCountriesResponse>(url);
 }

 /**
 * Fetches a list of signup requests with pagination and filtering.
 * @param page The current page number.
 * @param pageSize The number of items per page.
 * @param filters Optional filters (e.g., status, region, country).
 * @returns An Observable of the signup requests response.
 */
 getSignupRequests(
  page: number, 
  pageSize: number, 
  filters?: any, 
  context?: HttpContext): Observable<GetSignupRequestsResponse> {
  let params = new HttpParams()
   .set('page', page.toString())
   .set('page_size', pageSize.toString());

  if (filters) {
   for (const key in filters) {
    if (filters.hasOwnProperty(key) && filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
     const value = filters[key];

     if (Array.isArray(value) && value.length > 0 && value[0] instanceof Date) {
      const fromDate = value[0].toISOString().split('T')[0]; // Format to YYYY-MM-DD
      params = params.set(`${key}_from`, fromDate);
      if (value[1]) { // The 'to' date can be null if only one day is selected
       const toDate = value[1].toISOString().split('T')[0]; // Format to YYYY-MM-DD
       params = params.set(`${key}_to`, toDate);
      }
     }
     // Keep original check for a date range object for robustness
     else if (typeof value === 'object' && !Array.isArray(value) && value.from && value.to) {
      const fromDate = new Date(value.from).toISOString().split('T')[0];
      params = params.set(`${key}_from`, fromDate);
      const toDate = new Date(value.to).toISOString().split('T')[0];
      params = params.set(`${key}_to`, toDate);
     }
     // Original check for multiselect arrays
     else if (Array.isArray(value)) {
      params = params.set(key, value.join(','));
     }
     // Fallback for primitive types
     else {
      params = params.set(key, value);
     }
    }
   }
  }

 const url = getApiUrl('GET_SIGNUP_REQUESTS');
  
  // Include the context in the request options if provided
  const options = context ? { params, context } : { params };
  
  return this.http.get<GetSignupRequestsResponse>(url, options);
 }

/**
 * Fetches a list of onboarded users with pagination and filtering.
 * @param page The current page number.
 * @param pageSize The number of items per page.
 * @param filters Optional filters.
 * @returns An Observable of the onboarded users response.
 */
getOnboardedUsers(page: number, pageSize: number, filters?: any): Observable<GetOnboardedUsersResponse> {
  let params = new HttpParams()
    .set('page', page.toString())
    .set('page_size', pageSize.toString());

  if (filters) {
    for (const key in filters) {
      if (filters.hasOwnProperty(key) && filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
        const value = filters[key];

        // This part correctly handles date ranges (from and to)
        if (Array.isArray(value) && value.length > 0 && value[0] instanceof Date) {
          const fromDate = value[0].toISOString().split('T')[0];
          params = params.set(`${key}_from`, fromDate);
          if (value[1]) {
            const toDate = value[1].toISOString().split('T')[0];
            params = params.set(`${key}_to`, toDate);
          }
        }
        // This part handles the {from: Date, to: Date} object from the table component
        else if (typeof value === 'object' && !Array.isArray(value) && value.from && value.to) {
          const fromDate = new Date(value.from).toISOString().split('T')[0];
          params = params.set(`${key}_from`, fromDate);
          const toDate = new Date(value.to).toISOString().split('T')[0];
          params = params.set(`${key}_to`, toDate);
        }
        // This part handles multiselect arrays (e.g., ['value1', 'value2'])
        else if (Array.isArray(value)) {
          params = params.set(key, value.join(','));
        }
        // Handle pre-formatted date strings (e.g., 'added_at_from', 'added_at_to') - pass through directly
        else if (typeof value === 'string' && (key.endsWith('_from') || key.endsWith('_to'))) {
          params = params.set(key, value);
        }
        // Fallback for primitive types (string, number, boolean)
        else {
          params = params.set(key, value);
        }
      }
    }
  }

  const url = getApiUrl('GET_ONBOARDED_USERS');  
  return this.http.get<GetOnboardedUsersResponse>(url, { params });
}

 /**
 * Approves or declines a signup request.
 * @param requestId The ID of the signup request to update.
 * @param payload The action and remark for the update.
 * @returns An Observable of the update response.
 */
 updateSignupRequestStatus(requestId: number, payload: { action: string; admin_remark: string }): Observable<UpdateSignupStatusResponse> {
  const baseUrl = getApiUrl('UPDATE_SIGNUP_REQUEST');
  const url = `${baseUrl}/${requestId}`;
  return this.http.patch<UpdateSignupStatusResponse>(url, payload);
 }

 updateUser(userId: number, payload: Partial<UserUpdatePayload>): Observable<any> {
  const filteredPayload = Object.fromEntries(
   Object.entries(payload).filter(([, value]) => value !== undefined && value !== null)
  );
  const baseUrl = getApiUrl('GET_ONBOARDED_USERS');
  const url = `${baseUrl}${userId}`;
  return this.http.patch(url, filteredPayload);
 }

   // Attachments: get a short-lived signed URL for preview/download
  getSignedAttachmentDownloadById(attachmentId: string | number, context?: HttpContext): Observable<{ message: string; download_url: string; expires_in: string }>{
    const base = getApiUrl('ATTACHMENTS_DOWNLOAD');
    const encodedId = encodeURIComponent(String(attachmentId));
    const needsTrailingSlash = base.endsWith('/') ? '' : '/';
    const url = `${base}${needsTrailingSlash}${encodedId}/`;
   const options = context ? { context } : {};
   return this.http.get<{ message: string; download_url: string; expires_in: string }>(url, options);
  }

  getSignedAttachmentDownloadFromUrl(sourceUrl: string, context?: HttpContext): Observable<{ message: string; download_url: string; expires_in: string }>{
    const trimmed = sourceUrl?.trim();
    if (!trimmed) {
      return throwError(() => new Error('Attachment download URL is missing.'));
    }

    if (/^\d+$/.test(trimmed)) {
      return this.getSignedAttachmentDownloadById(trimmed, context);
    }

    const lowered = trimmed.toLowerCase();
    const marker = 'attachments/download';
    const markerIndex = lowered.indexOf(marker);
    if (markerIndex === -1) {
      return throwError(() => new Error('Unrecognized attachment URL format'));
    }

    const suffixStart = markerIndex + marker.length;
    const originalSuffix = trimmed.substring(suffixStart);
    let normalizedSuffix = originalSuffix.startsWith('/') ? originalSuffix : `/${originalSuffix}`;

    if (/^\/\d+\/?$/.test(normalizedSuffix) && !normalizedSuffix.endsWith('/')) {
      normalizedSuffix = `${normalizedSuffix}/`;
    }

    const base = getApiUrl('ATTACHMENTS_DOWNLOAD');
    const targetUrl = `${base}${normalizedSuffix}`;
    const options = context ? { context } : {};
    return this.http.get<{ message: string; download_url: string; expires_in: string }>(targetUrl, options);
   }
   /**
  * Admin creates a user directly (bypasses signup flow).
  */
 createUser(payload: SignupRequestPayload): Observable<CreateUserResponse> {
  const url = getApiUrl('CREATE_USER');
  return this.http.post<CreateUserResponse>(url, payload);
 }
}